import { ROUTES } from '@/app/routes';
import Pressable from '@/components/motion/pressable';
import { CardDescription, CardTitle } from '@/components/ui/card';
import { $features } from '@/entities/features-entity';
import { useLandingsAccess } from '@/hooks/use-landings-access';
import { cn } from '@/lib/utils';
import { useUnit } from 'effector-react';
import {
	BarChart3Icon,
	BellIcon,
	ChevronRightIcon,
	FileArchiveIcon,
	GlobeIcon,
	InfoIcon,
	LinkIcon,
	LayoutTemplateIcon,
	TargetIcon,
} from 'lucide-react';
import { useNavigate } from 'react-router';

type ChartColor = 'chart-1' | 'chart-2' | 'chart-4' | 'chart-5';

interface HubItem {
	label: string;
	description: string;
	path: string;
	icon: typeof GlobeIcon;
	color: ChartColor;
}

const HUB_ITEMS: HubItem[] = [
	{
		label: 'Уведомления TG',
		description: 'Бот и чат для событий',
		path: ROUTES.landingsNotification,
		icon: BellIcon,
		color: 'chart-2',
	},
	{
		label: 'Meta Pixel',
		description: 'Пиксели Facebook для рекламы',
		path: ROUTES.landingsMetaPixels,
		icon: TargetIcon,
		color: 'chart-4',
	},
	{
		label: 'Мои шаблоны',
		description: 'Свой ZIP для APK-лендинга',
		path: ROUTES.landingsTemplates,
		icon: FileArchiveIcon,
		color: 'chart-2',
	},
	{
		label: 'Telegram Auth ZIP',
		description: 'ZIP для входа: phone, code, 2FA',
		path: ROUTES.landingsTelegramAuthTemplates,
		icon: FileArchiveIcon,
		color: 'chart-5',
	},
	{
		label: 'Домены',
		description: 'Домены и NS-записи',
		path: ROUTES.landingsDomains,
		icon: GlobeIcon,
		color: 'chart-1',
	},
	{
		label: 'Сайты',
		description: 'Поддомены и шаблоны',
		path: ROUTES.landingsSites,
		icon: LayoutTemplateIcon,
		color: 'chart-4',
	},
	{
		label: 'Ссылки',
		description: 'Создание трекинг-ссылок',
		path: ROUTES.landingsLinks,
		icon: LinkIcon,
		color: 'chart-5',
	},
	{
		label: 'Статистика',
		description: 'События connect / download / form',
		path: ROUTES.landingsEvents,
		icon: BarChart3Icon,
		color: 'chart-1',
	},
];

const COLOR_CLASSES: Record<ChartColor, { iconBg: string; title: string }> = {
	'chart-1': { iconBg: 'bg-chart-1/15', title: 'text-chart-1' },
	'chart-2': { iconBg: 'bg-chart-2/15', title: 'text-chart-2' },
	'chart-4': { iconBg: 'bg-chart-4/15', title: 'text-chart-4' },
	'chart-5': { iconBg: 'bg-chart-5/15', title: 'text-chart-5' },
};

export default function LandingsHubPage() {
	const navigate = useNavigate();
	const features = useUnit($features);
	const { enabled, accessible, loading, error } = useLandingsAccess();
	const disabled = !enabled || !accessible;

	function go(path: string) {
		if (disabled) return;
		navigate(path);
	}

	return (
		<div className='flex flex-col gap-3'>
			{!enabled ? (
				<div className='flex items-start gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2.5 text-sm text-muted-foreground'>
					<InfoIcon className='mt-0.5 size-4 shrink-0' />
					<span>Модуль лендингов отключён администратором. Обратитесь к администратору для включения.</span>
				</div>
			) : null}

			{enabled && !loading && !accessible ? (
				<div className='flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-sm text-destructive'>
					<InfoIcon className='mt-0.5 size-4 shrink-0' />
					<span>{error ?? 'Не удалось получить токен для лендингов. Попробуйте обновить страницу.'}</span>
				</div>
			) : null}

			{disabled && enabled && loading ? (
				<p className='text-xs text-muted-foreground px-0.5'>Проверка доступа…</p>
			) : null}

			<div className='flex flex-col gap-2'>
				{HUB_ITEMS.map(item => {
					const Icon = item.icon;
					const colors = COLOR_CLASSES[item.color];
					return (
						<Pressable
							key={item.path}
							role='button'
							tabIndex={disabled ? -1 : 0}
							onClick={() => go(item.path)}
							className={cn(
								'rounded-xl border border-border bg-card shadow-sm overflow-hidden text-left',
								disabled ? 'opacity-50 cursor-not-allowed' : 'active:bg-accent/30 cursor-pointer',
							)}
						>
							<div className='flex items-center justify-between gap-3 px-3 py-3'>
								<div className='flex items-center gap-3 min-w-0'>
									<div className={cn('rounded-lg p-2 shrink-0', colors.iconBg)}>
										<Icon className={colors.title} size={20} />
									</div>
									<div className='min-w-0'>
										<CardTitle className={cn('text-sm', colors.title)}>{item.label}</CardTitle>
										<CardDescription className='truncate'>{item.description}</CardDescription>
									</div>
								</div>
								<ChevronRightIcon size={18} className='text-muted-foreground shrink-0' />
							</div>
						</Pressable>
					);
				})}
			</div>

			{features.landingsEnabled && accessible ? (
				<p className='text-xs text-muted-foreground px-0.5'>
					Лендинги - отдельный контур от доменов APK. Домены, сайты и ссылки для веб-лендингов.
				</p>
			) : null}
		</div>
	);
}
